import React from 'react';
import { labels } from '../../constants/messages';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  fullScreen?: boolean;
}

// Ancho del rastro proporcional a la pantalla, con mínimo y máximo; el alto
// sale del viewBox (proporción ~2:1). `sm` es fijo porque va junto a texto.
const sizeClasses = {
  sm: 'w-10',
  md: 'w-[clamp(4.5rem,18vw,7.5rem)]',
  lg: 'w-[clamp(6rem,26vw,12rem)]',
};

// Cuatro pisadas que avanzan hacia la derecha alternando lado izquierdo/derecho
const STEPS = [
  { x: 2, y: 30, delay: 0 },
  { x: 30, y: 8, delay: 0.3 },
  { x: 58, y: 30, delay: 0.6 },
  { x: 86, y: 8, delay: 0.9 },
];

const Paw: React.FC = () => (
  <>
    <ellipse cx="12" cy="15.5" rx="5.6" ry="4.9" />
    <ellipse cx="5.6" cy="9.6" rx="2.2" ry="2.6" transform="rotate(-25 5.6 9.6)" />
    <ellipse cx="9.6" cy="5.4" rx="2.2" ry="2.7" transform="rotate(-8 9.6 5.4)" />
    <ellipse cx="14.4" cy="5.4" rx="2.2" ry="2.7" transform="rotate(8 14.4 5.4)" />
    <ellipse cx="18.4" cy="9.6" rx="2.2" ry="2.6" transform="rotate(25 18.4 9.6)" />
  </>
);

const PawTrail: React.FC<{ size: keyof typeof sizeClasses }> = ({ size }) => (
  <svg
    viewBox="0 0 112 62"
    className={`${sizeClasses[size]} h-auto text-lp-teal fill-current`}
    aria-hidden="true"
  >
    {STEPS.map((step, i) => (
      <g key={i} transform={`translate(${step.x} ${step.y}) rotate(90 12 12)`}>
        <g
          className="animate-lp-paw-step motion-reduce:animate-none origin-center [transform-box:fill-box]"
          // La opacidad fija solo se ve sin animación (prefers-reduced-motion)
          style={{ animationDelay: `${step.delay}s`, opacity: 0.4 + i * 0.2 }}
        >
          <Paw />
        </g>
      </g>
    ))}
  </svg>
);

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  className = '',
  fullScreen = false,
}) => {
  const content = (
    <>
      <PawTrail size={size} />
      <span className="sr-only">{labels.loading}</span>
    </>
  );

  if (fullScreen) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="fixed inset-0 m-0 flex items-center justify-center bg-lp-paper z-[9999]"
      >
        {content}
      </div>
    );
  }

  return (
    <div role="status" aria-live="polite" className={`flex justify-center items-center ${className}`}>
      {content}
    </div>
  );
};

export default LoadingSpinner;
